import React, { useEffect, useState, useRef } from "react";
import {
  GetPaymentVoucherList,
  BankAccountList,
  PaymentVoucherExport,
} from "../../../Services/Services";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  Tooltip,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Menu,
  MenuItem,
  Divider,
  Grid,
  CircularProgress,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Print,
  Visibility,
  Search,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { generatePaymentPdf } from "../Accounts/PaymentVoucherPrint";
import { Description as FileIcon } from "@mui/icons-material";
import { Flex } from "antd";
const DailyPayment = () => {
  // ===== States =====
  const [PaymentVouchers, setPaymentVouchers] = useState([]);
  const [bankLists, setBankLists] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false); 
  const [excelLoading, setExcelLoading] = useState(false);
  const [paymentModeFilter, setPaymentModeFilter] = useState("all"); // all, cash, bank

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedVoucher, setExpandedVoucher] = useState(null);

  // Date filters
  const [filters, setFilters] = useState({
    date_range: "today",
  });
  const dateRangeBoxRef = useRef(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // ⭐ ADD THESE TWO NEW STATES
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null); // ✅ 'from' or 'to' or null
  const dateInputRef = useRef(null);
  const scrollRef = useRef(null);

  // ===== Fetch Bank List =====
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const banks = await BankAccountList();

        // ✅ Handle pagination response
        const bankData = banks?.results || banks || [];
        setBankLists(bankData);
      } catch (error) {
        console.error("❌ Error fetching banks:", error);
        toast.error("Failed to load banks");
        setBankLists([]); // ✅ Set empty array on error
      }
    };
    fetchBanks();
  }, []);

  const handleExcelExport = async () => {
    if (PaymentVouchers.length === 0) {
      toast.warning("No data available to export");
      return;
    }

    setExcelLoading(true);
    try {
      const exportFilters = {
        date_range: filters.date_range || "today",
      };

      if (paymentModeFilter === "cash") {
        exportFilters.payment_mode = "Cash";
      } else if (paymentModeFilter === "bank") {
        exportFilters.payment_mode = "Bank";
        if (selectedBank && selectedBank.id) {
          exportFilters.bank_id = selectedBank.id;
        }
      }
      const blob = await PaymentVoucherExport(exportFilters, customDateRange);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payment_Vouchers_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  // ✅ Fetch all vouchers by default when component loads
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVouchers(filters, 1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange, paymentModeFilter, selectedBank]);

  // ✅ Reset bank selection when payment mode changes
  useEffect(() => {
    if (paymentModeFilter !== "bank") {
      setSelectedBank(null);
    }
  }, [paymentModeFilter]);

  const fetchVouchers = async (
    filterObj = filters,
    pageNum = 1,
    reset = false,
  ) => {
    try {
      if (reset) {
      setFilterLoading(true);
    } else {
      setLoading(true);
    }
      const params = {};
      params.date_range = filterObj.date_range; // ✅ Add this line
      // ✅ Payment Mode Filter
      if (paymentModeFilter === "cash") {
        params.payment_mode = "Cash";
      } else if (paymentModeFilter === "bank") {
        params.payment_mode = "Bank";

        // ✅ ONLY add bank_id if a bank is actually selected
        if (selectedBank && selectedBank.id) {
          params.bank_id = selectedBank.id;
        }
        // ✅ If no bank selected, show ALL banks (no bank_id filter)
      }
      // else 'all' - no payment_mode filter
      // ✅ Add custom dates if custom range selected
      if (
        filterObj.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      // ✅ Debug log

      const data = await GetPaymentVoucherList(params, pageNum);

      const results = data?.results || [];
      const next = data?.next ?? null;

      if (reset || pageNum === 1) {
        setPaymentVouchers(results);
      } else {
        setPaymentVouchers((prev) => [...prev, ...results]);
      }

      setHasMore(Boolean(next));
      setPage(pageNum);
    } catch (error) {
      console.error("❌ Failed to fetch vouchers:", error);
      toast.error("Failed to load vouchers");
    } finally {
       setFilterLoading(false);
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    fetchVouchers(filters, nextPage);
  };

  // ===== Reset Filters =====
  const handleResetFilters = () => {
    setFilters({
      date_range: "today", // ✅ Fixed
    });
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ⭐ ADD THIS LINE
    setShowCustomDateInputs(false); // ⭐ ADD THIS LINE
    setSelectingDate(null);
    setPaymentModeFilter("all");
    setSelectedBank(null);
    setPaymentVouchers([]);
    setHasMore(false);
  };

  // ===== Helper Functions =====
  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const formatDates = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  //Date filter
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);

      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);

      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Monday
        return `${formatDate(weekStart)} to ${formatDate(now)}`;

      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay()); // Last Sunday
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6); // Last Monday
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;

      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;

      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;

      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          // April to December
          fyStart = new Date(now.getFullYear(), 3, 1); // April 1
        } else {
          // January to March
          fyStart = new Date(now.getFullYear() - 1, 3, 1); // Last year April 1
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;

      default:
        return "";
    }
  };

  const totalAmount = PaymentVouchers.reduce(
    (sum, v) => sum + parseFloat(v.total_amount || 0),
    0,
  );
  // ⭐ ADD THESE TWO FUNCTIONS AFTER formatDateDisplay

  // ✅ Apply Custom Date Range
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({ ...tempCustomDateRange });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  return (
    <>
      <ToastContainer />

      {/* ========== FILTERS SECTION ========== */}
      <Grid container spacing={2} sx={{ mt: { xs: -3, md: -5 }, mb: 2 }}>
        {/* ✅ Payment Mode Radio Buttons */}
        {/* ✅ Date Range */}
        <Grid item xs={12} sm={3}>
          <TextField
            label="Date Range"
            value={
              filters.date_range === "custom" &&
              customDateRange.from &&
              customDateRange.to
                ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                : filters.date_range === "today"
                  ? "Today"
                  : filters.date_range === "yesterday"
                    ? "Yesterday"
                    : filters.date_range === "this_week"
                      ? "This Week"
                      : filters.date_range === "previous_week"
                        ? "Previous Week"
                        : filters.date_range === "this_month"
                          ? "This Month"
                          : filters.date_range === "current_year"
                            ? "Current Year"
                            : filters.date_range === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": {
                height: 35,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
            fullWidth
          />
        </Grid>

        {/* ⭐ REPLACE OLD MENU WITH THIS */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: { width: 350, maxHeight: 500 },
          }}
        >
          {/* Quick Ranges */}
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((item) => (
            <MenuItem
              key={item.key}
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: item.key }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">{item.label}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {getDateRangeDisplay(item.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          {/* ✅ Custom Date Range - Expandable */}
          <MenuItem
            onClick={() => setShowCustomDateInputs((prev) => !prev)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
            >
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Select Date Range
              </Typography>

              {/* From and To Date Inputs Side by Side */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: tempCustomDateRange.to || undefined,
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              {/* Apply and Cancel Buttons */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={
                    !tempCustomDateRange.from || !tempCustomDateRange.to
                  }
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>

        <Grid item xs={12} sm={6} display={{ xs: "block", sm: "Flex" }}>
          <Box sx={{ width: { xs: "100%", sm: "auto" }, mb: { xs: 2, sm: 0 } }}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={paymentModeFilter}
                onChange={(e) => setPaymentModeFilter(e.target.value)}
                sx={{
                  display: "flex",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label="All"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    },
                  }}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio size="small" />}
                  label="Cash"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    },
                  }}
                />
                <FormControlLabel
                  value="bank"
                  control={<Radio size="small" />}
                  label="Bank"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* ✅ Bank Autocomplete */}
          <Box sx={{ width: "100%", ml: { xs: 0, sm: 2 } }}>
            <Autocomplete
              options={bankLists || []}
              value={selectedBank}
              onChange={(e, newVal) => setSelectedBank(newVal)}
              getOptionLabel={(option) =>
                `${option.bank_name} - ${option.account_number || "N/A"}`
              }
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              disabled={paymentModeFilter !== "bank"}
              clearOnEscape
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bank"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.65rem", sm: "0.7rem" },
                    },
                  }}
                />
              )}
            />
          </Box>
        </Grid>

        {/* ✅ Reset Button */}
        <Grid item xs={6} sm={6} md={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
            fullWidth
            sx={{
              height: 35,
              borderRadius: 0,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
              textTransform: "none",
              px: { xs: 1, sm: 2 },
            }}
          >
            Reset
          </Button>
        </Grid>
        <Grid item xs={6} sm={6} md={1}>
          <IconButton
            color="success"
            size="small"
            onClick={handleExcelExport}
            disabled={excelLoading || PaymentVouchers.length === 0}
          >
            {excelLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileIcon fontSize="small" />
            )}
          </IconButton>
        </Grid>
      </Grid>

      {/* ========== TABLE WITH INFINITE SCROLL ========== */}

     <Box sx={{ position: "relative", flex: 1, minHeight: "400px" }}>
  
  {/* ✅ LOADING OVERLAY - Only shows for filter changes */}
  {filterLoading && (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 1000,
        borderRadius: 1,
      }}
    >
      <CircularProgress size={50} thickness={4} color="primary" />
      <Typography sx={{ mt: 2, color: "#333", fontWeight: 500 }}>
        {filters.date_range === "financial_year"
          ? "Loading Financial Year Data..."
          : filters.date_range === "current_year"
          ? "Loading Current Year Data..."
          : filters.date_range === "this_month"
          ? "Loading This Month Data..."
          : filters.date_range === "custom"
          ? "Loading Custom Date Range..."
          : "Loading Data..."}
      </Typography>
      <Typography sx={{ mt: 0.5, color: "#666", fontSize: "0.8rem" }}>
        Please wait, this may take a moment
      </Typography>
    </Box>
  )}
        {/* ✅ CONDITIONAL RENDERING - Mobile vs Desktop */}
        {isMobile ? (
          /* 📱 MOBILE VIEW - Expandable List */
          <InfiniteScroll
            dataLength={PaymentVouchers.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Typography
                sx={{
                  textAlign: "center",
                  py: 2,
                  fontSize: "0.875rem",
                  color: "#666",
                }}
              >
                Loading...
              </Typography>
            }
            endMessage={
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#999",
                    mb: 2,
                  }}
                >
                  No more records!
                </Typography>
                {/* Total Amount */}
                <Box
                  sx={{
                    bgcolor: "#E3F2FD",
                    p: 2,
                    borderRadius: 1,
                    mx: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      color: "#1976d2",
                    }}
                  >
                    Total Amount: ₹{" "}
                    {totalAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
            }
            height="calc(100vh - 200px)"
            style={{ overflow: "auto" }}
          >
            <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
              {PaymentVouchers.length > 0 ? (
                PaymentVouchers.map((voucher, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: index === PaymentVouchers.length - 1 ? 2 : 0,
                    }}
                  >
                    {/* Main Row */}
                    <Box
                      onClick={() =>
                        setExpandedVoucher(
                          expandedVoucher === index ? null : index,
                        )
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        cursor: "pointer",
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:active": {
                          bgcolor: "#e3f2fd",
                        },
                        borderLeft: "4px solid #1976d2",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#666",
                              minWidth: 25,
                            }}
                          >
                            #{index + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                              flex: 1,
                            }}
                          >
                            {voucher.payment_number}
                          </Typography>

                          <Chip
                            label={voucher.status || "Pending"}
                            color={
                              voucher.status === "Approved"
                                ? "success"
                                : voucher.status === "Rejected"
                                  ? "error"
                                  : "warning"
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: "0.6rem",
                              height: 20,
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                          }}
                        >
                          Amount: ₹{" "}
                          {Number(voucher.total_amount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                      </Box>

                      <IconButton size="small" sx={{ ml: 1 }}>
                        {expandedVoucher === index ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    {/* Expanded Details */}
                    <Collapse
                      in={expandedVoucher === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                          borderLeft: "4px solid #1976d2",
                        }}
                      >
                        {/* 2 Column Grid for Details */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 0.8,
                            mb: 1,
                          }}
                        >
                          {/* Bank Name */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              BANK
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.bank_name || voucher.bank_name_fk || "-"}
                            </Typography>
                          </Box>

                          {/* Payment Mode */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              MODE
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.payment_mode}
                            </Typography>
                          </Box>

                          {/* Project */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              PROJECT
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {truncateText(voucher.project_name, 15)}
                            </Typography>
                          </Box>

                          {/* Division */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              DIVISION
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.division_name}
                            </Typography>
                          </Box>

                          {/* Date */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              DATE
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {formatDates(voucher.created_at)}
                            </Typography>
                          </Box>

                          {/* Created By */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              CREATED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.created_by_name}
                            </Typography>
                          </Box>

                          {/* Approved By */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              APPROVED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {voucher.approved_name || "-"}
                            </Typography>
                          </Box>

                          {/* Paid Amount */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              PAID AMOUNT
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#1976d2",
                              }}
                            >
                              ₹{" "}
                              {Number(voucher.total_amount || 0).toLocaleString(
                                "en-IN",
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Ledger Name - Full Width */}
                        <Box sx={{ mb: 0.8 }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            LEDGER NAME
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#555",
                              lineHeight: 1.3,
                              fontWeight: 500,
                            }}
                          >
                            {voucher.supplier_name ||
                              voucher.expense_name ||
                              voucher.advanceworker_name ||
                              voucher.imprest_name ||
                              voucher.party_name ||
                              "-"}
                          </Typography>
                        </Box>

                        {/* Remarks - Full Width */}
                        {voucher.remarks && (
                          <Box sx={{ mb: 0.8 }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                                mb: 0.3,
                              }}
                            >
                              REMARKS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#555",
                                lineHeight: 1.3,
                              }}
                            >
                              {voucher.remarks}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                        {/* Action Buttons */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                            }}
                          >
                            ACTIONS
                          </Typography>

                          <Box sx={{ display: "flex", gap: 0.6 }}>
                            {/* Print Button */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                generatePaymentPdf(voucher);
                              }}
                              sx={{
                                bgcolor: "#fff3e0",
                                borderRadius: "6px",
                                width: 32,
                                height: 32,
                                "&:hover": {
                                  bgcolor: "#ffe0b2",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <Print
                                sx={{
                                  fontSize: "1rem",
                                  color: "#f57c00",
                                }}
                              />
                            </IconButton>

                            {/* View Attachment Button */}
                            {voucher.attachment && (
                              <a
                                href={voucher.attachment}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconButton
                                  size="small"
                                  sx={{
                                    bgcolor: "#e3f2fd",
                                    borderRadius: "6px",
                                    width: 32,
                                    height: 32,
                                    "&:hover": {
                                      bgcolor: "#bbdefb",
                                      transform: "scale(1.05)",
                                    },
                                    transition: "all 0.2s",
                                  }}
                                >
                                  <Visibility
                                    sx={{
                                      fontSize: "1rem",
                                      color: "#1976d2",
                                    }}
                                  />
                                </IconButton>
                              </a>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>

                    <Divider />
                  </Box>
                ))
              ) : (
                <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
                  <Typography>No matching records found</Typography>
                </Box>
              )}
            </Box>
          </InfiniteScroll>
        ) : (
          /* 💻 DESKTOP VIEW - YOUR ORIGINAL TABLE */
          <InfiniteScroll
            dataLength={PaymentVouchers.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Typography align="center" sx={{ py: 2 }}>
                Loading...
              </Typography>
            }
            scrollableTarget="scrollableDiv"
          >
            <TableContainer
              ref={scrollRef}
              id="scrollableDiv"
              component={Paper}
              sx={{ maxHeight: "88vh", overflow: "auto", position: "relative" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1600,
                  "& td, & th": {
                    fontWeight: "400",
                    borderBottom: "1px solid #ccc",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: "#f5f5f5",
                    padding: "0px 16px",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold !important",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell>Voucher No</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Paid Amt</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Ledger Name</TableCell>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        width: 150,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {PaymentVouchers.map((voucher, index) => (
                    <TableRow
                      hover
                      key={index}
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,

                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff", // alternate row color
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          paddingTop: "2px !important",
                          paddingBottom: "2px !important",
                          fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // responsive font
                          whiteSpace: "nowrap",
                          padding: "0px 16px",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          zIndex: 2,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{voucher.payment_number}</TableCell>
                      <TableCell>
                        {voucher.bank_name || voucher.bank_name_fk || "-"}
                      </TableCell>{" "}
                      {/* ✅ NEW FIELD */}
                      <TableCell>
                        <Chip
                          label={voucher.status || "Pending"}
                          color={
                            voucher.status === "Approved"
                              ? "success"
                              : voucher.status === "Rejected"
                                ? "error"
                                : "warning"
                          }
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {Number(voucher.total_amount || 0).toLocaleString(
                          "en-IN",
                        )}
                      </TableCell>
                      <TableCell>{voucher.project_name}</TableCell>
                      <TableCell>{voucher.division_name}</TableCell>
                      <TableCell>
                        {voucher.supplier_name ||
                          voucher.expense_name ||
                          voucher.advanceworker_name ||
                          voucher.imprest_name ||
                          voucher.party_name}
                      </TableCell>
                      <TableCell>{voucher.payment_mode}</TableCell>
                      <TableCell>{formatDates(voucher.created_at)}</TableCell>
                      <TableCell>{voucher.created_by_name}</TableCell>
                      <TableCell>{voucher.approved_name}</TableCell>
                      <TableCell>
                        <Tooltip title={voucher.remarks || ""} arrow>
                          <span>{truncateText(voucher.remarks || "", 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          color="warning"
                          size="small"
                          onClick={() => generatePaymentPdf(voucher)}
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        >
                          <Print fontSize="small" />
                        </IconButton>
                        {voucher.attachment && (
                          <a
                            href={voucher.attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              color="info"
                              size="small"
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Total Row */}
                  <TableRow
                    sx={{
                      "& td": {
                        backgroundColor: "#E3F2FD !important",
                        color: "#000 !important",
                        fontWeight: "bold !important",
                        pt: 0.6,
                        pb: 0.6,
                      },
                    }}
                  >
                    <TableCell
                      colSpan={4}
                      align="right"
                      sx={{ fontWeight: "bold !important" }}
                    >
                      Totals:
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold !important" }}>
                      {PaymentVouchers.reduce(
                        (sum, v) => sum + parseFloat(v.total_amount || 0),
                        0,
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell colSpan={9}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        )}
      </Box>
    </>
  );
};

export default DailyPayment;
